import { featureEquals } from "../Feature";

import { MODEL_STR } from "./Role";

import type { Features } from "../Feature";

export type AccountPermissionGroup = "moneyMovement" | "manageTemplates";

export type AccountPermissionParent =
  | "sendAchPayments"
  | "collectAchPayments"
  | "sendDomesticWires"
  | "sendInternationalWires"
  | "importPaymentFiles";

export type AccountPermissionOption = {
  modelStr: string;
  group: AccountPermissionGroup;
  /**
   * Visual parent. The parent is not a permission: it reads as checked while any child is, and
   * toggling it sets or clears all its (available) children. The saved data stays flat.
   */
  parent?: AccountPermissionParent;
  /** Whether the institution has the feature(s) this permission depends on. */
  isAvailable: (features: Features) => boolean;
};

const on = (features: Features, ...flags: string[]) =>
  flags.every((flag) => featureEquals(features, flag));
const fedwireTemplates = (features: Features) =>
  featureEquals(features, "wire_templates", "fedwire");

/**
 * Account-scoped permissions in display order, mirroring the server's
 * `ACCOUNT_SCOPED_PERMISSION_CATALOG` (labels live with each client's UI). Feature gating mirrors
 * the legacy manage users checklist. Anything not listed here (recipients, cards, BillPay, Zelle,
 * statements, approvals...) stays a role-level permission.
 */
export const ACCOUNT_PERMISSION_OPTIONS: ReadonlyArray<AccountPermissionOption> =
  [
    {
      modelStr: MODEL_STR.INTRA_INSTITUTION_TRANSFER,
      group: "moneyMovement",
      isAvailable: (f) => on(f, "internal_transfers"),
    },
    {
      modelStr: MODEL_STR.ACH_TRANSFER,
      group: "moneyMovement",
      isAvailable: (f) => on(f, "ach"),
    },
    {
      modelStr: MODEL_STR.ACH_PAYMENT,
      group: "moneyMovement",
      parent: "sendAchPayments",
      isAvailable: (f) => on(f, "ach_payments"),
    },
    {
      modelStr: MODEL_STR.ACH_PAYMENT_VIA_TEMPLATE,
      group: "moneyMovement",
      parent: "sendAchPayments",
      isAvailable: (f) => on(f, "ach_payments", "ach_templates"),
    },
    {
      modelStr: MODEL_STR.UNVERIFIED_FEDNOW_SEND,
      group: "moneyMovement",
      parent: "sendAchPayments",
      isAvailable: (f) =>
        on(
          f,
          "ach_payments",
          "bulk_payments",
          "expedited_payments",
          "fednow_send",
        ),
    },
    {
      modelStr: MODEL_STR.ACH_PAYMENT_REVERSE,
      group: "moneyMovement",
      parent: "sendAchPayments",
      isAvailable: (f) => on(f, "ach_payments", "user_initiated_ach_reversals"),
    },
    {
      modelStr: MODEL_STR.ACH_PAYMENT_COLLECT,
      group: "moneyMovement",
      parent: "collectAchPayments",
      isAvailable: (f) => on(f, "ach_payments_collect"),
    },
    {
      modelStr: MODEL_STR.ACH_PAYMENT_COLLECT_VIA_TEMPLATE,
      group: "moneyMovement",
      parent: "collectAchPayments",
      isAvailable: (f) => on(f, "ach_payments_collect", "ach_templates"),
    },
    {
      modelStr: MODEL_STR.ACH_PAYMENT_COLLECT_REVERSE,
      group: "moneyMovement",
      parent: "collectAchPayments",
      isAvailable: (f) =>
        on(f, "ach_payments_collect", "user_initiated_ach_reversals"),
    },
    {
      modelStr: MODEL_STR.DOMESTIC_WIRE_TRANSACTION,
      group: "moneyMovement",
      parent: "sendDomesticWires",
      isAvailable: (f) => on(f, "wires"),
    },
    {
      modelStr: MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES,
      group: "moneyMovement",
      parent: "sendDomesticWires",
      isAvailable: (f) => on(f, "wires") && fedwireTemplates(f),
    },
    {
      modelStr: MODEL_STR.INTERNATIONAL_WIRE,
      group: "moneyMovement",
      parent: "sendInternationalWires",
      isAvailable: (f) => on(f, "international_wires"),
    },
    {
      modelStr: MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES,
      group: "moneyMovement",
      parent: "sendInternationalWires",
      isAvailable: (f) =>
        on(f, "international_wires", "international_wire_templates"),
    },
    {
      modelStr: MODEL_STR.ACH_PAYMENT_VIA_IMPORT,
      group: "moneyMovement",
      parent: "importPaymentFiles",
      isAvailable: (f) => on(f, "ach_payments", "ach_file_import"),
    },
    {
      modelStr: MODEL_STR.ACH_PAYMENT_COLLECT_VIA_IMPORT,
      group: "moneyMovement",
      parent: "importPaymentFiles",
      isAvailable: (f) => on(f, "ach_payments_collect", "ach_file_import"),
    },
    {
      modelStr: MODEL_STR.DOMESTIC_WIRE_IMPORT,
      group: "moneyMovement",
      parent: "importPaymentFiles",
      isAvailable: (f) => on(f, "wires", "domestic_wire_file_import"),
    },
    {
      modelStr: MODEL_STR.RDC,
      group: "moneyMovement",
      isAvailable: (f) => on(f, "rdc"),
    },
    {
      modelStr: MODEL_STR.BULK_PAYMENT_TEMPLATE,
      group: "manageTemplates",
      isAvailable: (f) => on(f, "ach_templates"),
    },
    {
      modelStr: MODEL_STR.DOMESTIC_WIRE_TEMPLATES,
      group: "manageTemplates",
      isAvailable: (f) => on(f, "wires") && fedwireTemplates(f),
    },
    {
      modelStr: MODEL_STR.INTERNATIONAL_WIRE_TEMPLATES,
      group: "manageTemplates",
      isAvailable: (f) => on(f, "international_wire_templates"),
    },
  ];

/** Permissions to offer an institution, in display order, bucketed by group. */
export const getAvailableAccountPermissions = (
  features: Features = {},
): Record<AccountPermissionGroup, AccountPermissionOption[]> => {
  const grouped: Record<AccountPermissionGroup, AccountPermissionOption[]> = {
    moneyMovement: [],
    manageTemplates: [],
  };
  ACCOUNT_PERMISSION_OPTIONS.forEach((option) => {
    if (option.isAvailable(features)) grouped[option.group].push(option);
  });
  return grouped;
};
